import React from 'react';

import { Refresh } from '@mui/icons-material';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';

export interface ErrorFallbackProps {
	title: string;
	description?: string;
	error?: Error;
	onRetry?: () => void;
	fullHeight?: boolean;
}

export function ErrorFallback({
	title,
	description = 'This area could not be displayed. You can try again without leaving the rest of the portal.',
	error,
	onRetry,
	fullHeight = false,
}: ErrorFallbackProps) {
	return (
		<Box
			sx={{
				flex: fullHeight ? 1 : undefined,
				minHeight: fullHeight ? 0 : 180,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				p: 3,
			}}
		>
			<Stack spacing={1.5} alignItems="center" sx={{ maxWidth: 480, textAlign: 'center' }}>
				<Typography variant="h6">{title}</Typography>
				<Typography variant="body2" color="text.secondary">
					{description}
				</Typography>
				{onRetry && (
					<Button variant="outlined" size="small" startIcon={<Refresh />} onClick={onRetry}>
						Try again
					</Button>
				)}
				{process.env.NODE_ENV !== 'production' && error && (
					<Alert severity="error" sx={{ width: '100%', textAlign: 'left', '& .MuiAlert-message': { overflowWrap: 'anywhere' } }}>
						{error.message}
					</Alert>
				)}
			</Stack>
		</Box>
	);
}

interface ErrorBoundaryProps extends React.PropsWithChildren {
	title?: string;
	description?: string;
	resetKeys?: readonly unknown[];
	onReset?: () => void;
	fullHeight?: boolean;
}

interface ErrorBoundaryState {
	error?: Error;
}

/** Isolates a render failure to a recoverable visual zone. */
export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
	public state: ErrorBoundaryState = {};

	public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { error };
	}

	public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
		console.error(`Unhandled rendering error in ${this.props.title ?? 'portal zone'}.`, error, errorInfo);
	}

	public componentDidUpdate(previousProps: ErrorBoundaryProps) {
		if (this.state.error && didResetKeysChange(previousProps.resetKeys, this.props.resetKeys)) {
			this.setState({ error: undefined });
		}
	}

	private handleRetry = () => {
		this.props.onReset?.();
		this.setState({ error: undefined });
	};

	public render() {
		if (this.state.error) {
			return (
				<ErrorFallback
					title={this.props.title ?? 'Something went wrong'}
					description={this.props.description}
					error={this.state.error}
					onRetry={this.handleRetry}
					fullHeight={this.props.fullHeight}
				/>
			);
		}

		return this.props.children;
	}
}

function didResetKeysChange(previousKeys: readonly unknown[] | undefined, nextKeys: readonly unknown[] | undefined) {
	if (previousKeys === nextKeys) {
		return false;
	}

	if (!previousKeys || !nextKeys || previousKeys.length !== nextKeys.length) {
		return true;
	}

	return previousKeys.some((key, index) => !Object.is(key, nextKeys[index]));
}
